import React, { useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { ChevronLeft, ChevronRight, Loader, ZoomIn, ZoomOut } from 'lucide-react';
import './PDFViewer.css';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface PDFViewerProps {
    file: File;
    onTotalPagesChange?: (totalPages: number) => void;
}

const PDFViewer: React.FC<PDFViewerProps> = ({ file, onTotalPagesChange }) => {
    const [numPages, setNumPages] = useState<number>(0);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [scale, setScale] = useState<number>(1.0);
    const [isLoading, setIsLoading] = useState(true);

    const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
        setNumPages(numPages);
        setPageNumber(1);
        setIsLoading(false);
        if (onTotalPagesChange) {
            onTotalPagesChange(numPages);
        }
    };

    const changePage = (offset: number) => {
        setPageNumber(prevPageNumber => {
            const newPage = prevPageNumber + offset;
            return Math.max(1, Math.min(newPage, numPages));
        });
    };

    const previousPage = () => changePage(-1);
    const nextPage = () => changePage(1);

    const zoomIn = () => setScale(prev => Math.min(prev + 0.1, 2.0));
    const zoomOut = () => setScale(prev => Math.max(prev - 0.1, 0.5));

    return (
        <div className="pdf-viewer-container">
            {isLoading && (
                <div className="pdf-loading">
                    <Loader className="spinner" />
                    <span>加载 PDF 中...</span>
                </div>
            )}

            <div className="pdf-document-wrapper">
                <Document
                    file={file}
                    onLoadSuccess={onDocumentLoadSuccess}
                    loading={<div className="loading-placeholder" />}
                    className="pdf-document"
                >
                    <Page
                        pageNumber={pageNumber}
                        scale={scale}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                        className="pdf-page"
                    />
                </Document>
            </div>

            {numPages > 0 && (
                <div className="pdf-controls">
                    <div className="pdf-pagination">
                        <button
                            onClick={previousPage}
                            disabled={pageNumber <= 1}
                            className="control-btn"
                        >
                            <ChevronLeft size={20} />
                        </button>
                        <span className="page-info">
                            {pageNumber} / {numPages}
                        </span>
                        <button
                            onClick={nextPage}
                            disabled={pageNumber >= numPages}
                            className="control-btn"
                        >
                            <ChevronRight size={20} />
                        </button>
                    </div>

                    <div className="pdf-zoom">
                        <button onClick={zoomOut} className="control-btn">
                            <ZoomOut size={18} />
                        </button>
                        <span className="zoom-info">{Math.round(scale * 100)}%</span>
                        <button onClick={zoomIn} className="control-btn">
                            <ZoomIn size={18} />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PDFViewer;
